<script setup lang="ts">
  import { watch } from 'vue'
  import { RotateCcw } from 'lucide-vue-next'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { Badge } from '@/components/ui/badge'
  import { Button } from '@/components/ui/button'
  import QuestionnaireFlowPreviewContent from './QuestionnaireFlowPreviewContent.vue'
  import { useQuestionnairePreviewStore } from '@/store/questionnairePreviewStore'
  import { useTranslation } from '@/composables/useTranslation'
  import type { QuestionnaireSection } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      preview: 'Preview',
      previewMode: 'Preview Mode',
      previewModeDescription: 'Answers are not saved',
      restart: 'Restart'
    },
    de: {
      preview: 'Vorschau',
      previewMode: 'Vorschau-Modus',
      previewModeDescription: 'Antworten werden nicht gespeichert',
      restart: 'Neu starten'
    }
  })

  const props = defineProps<{
    open: boolean
    schema: QuestionnaireSection[]
  }>()

  const emit = defineEmits<{
    'update:open': [value: boolean]
  }>()

  const previewStore = useQuestionnairePreviewStore()

  // Initialize preview when dialog opens
  watch(
    () => props.open,
    (isOpen) => {
      if (isOpen && props.schema) {
        previewStore.initializePreview(props.schema)
      }
    },
    { immediate: true }
  )

  // Re-initialize when schema changes while dialog is open
  watch(
    () => props.schema,
    (newSchema) => {
      if (props.open && newSchema) {
        previewStore.initializePreview(newSchema)
      }
    },
    { deep: true }
  )

  function handleRestart() {
    previewStore.resetPreview()
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent class="flex h-[90vh] max-h-[800px] w-[90vw] max-w-[700px] flex-col gap-0 p-0">
      <!-- Header -->
      <DialogHeader
        class="flex flex-row items-center justify-between border-b border-gray-200 px-6 py-4"
      >
        <div class="flex items-center gap-3">
          <DialogTitle class="text-xl font-semibold text-gray-900">
            {{ t('preview') }}
          </DialogTitle>
          <Badge
            variant="secondary"
            class="bg-amber-100 text-amber-800"
          >
            {{ t('previewMode') }}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          class="mr-6 flex items-center gap-1.5 text-gray-600 hover:text-gray-900"
          @click="handleRestart"
        >
          <RotateCcw class="h-4 w-4" />
          <span>{{ t('restart') }}</span>
        </Button>
      </DialogHeader>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto bg-gray-50 p-6">
        <QuestionnaireFlowPreviewContent @restart="handleRestart" />
      </div>

      <!-- Footer with hint -->
      <div class="border-t border-gray-200 bg-white px-6 py-3">
        <p class="text-center text-xs text-gray-500">
          {{ t('previewModeDescription') }}
        </p>
      </div>
    </DialogContent>
  </Dialog>
</template>
